<template>
  <div class="rb-ob-form rb-match-page rb-session-page">
    <header>
      <h1 class="rb-ob-title">{{ $t('intake.onlineSession.roomTitle') }}</h1>
    </header>

    <p v-if="needsLogin" class="rb-ob-status" role="status">{{ $t('intake.portal.needsLogin') }}</p>
    <p v-else-if="!loading && !session" class="rb-ob-status" role="status">{{ $t('intake.onlineSession.noSession') }}</p>

    <section class="rb-session-outcome">
      <h2>{{ therapistName }}</h2>
      <p>{{ lang === 'fa' ? 'جلسه در آلوکم برگزار می‌شود. وضعیت اتصال، میکروفون و دوربین و کنترل آن‌ها را در همان اتاق بررسی کن.' : 'Your session takes place in Alocam. Check your connection, microphone and camera and use their controls in that room.' }}</p>
    </section>

    <ul class="rb-session-meta" :aria-label="$t('intake.onlineSession.roomMetaAria')">
      <li>
        <span class="rb-session-meta-k">{{ $t('intake.onlineSession.roomConn') }}</span>
        <span class="rb-session-meta-v">{{ connLabel }}</span>
      </li>
      <li>
        <span class="rb-session-meta-k">{{ $t('intake.onlineSession.waitTime') }}</span>
        <span class="rb-session-meta-v">{{ sessionTime }}</span>
      </li>
    </ul>

    <div class="rb-ob-actions">
      <button
          v-if="session"
          class="rb-ob-submit"
          type="button"
          :disabled="joining || loading || !canEnter"
          @click="openJoin"
      >
        {{ $t('intake.onlineSession.openJoinLink') }}
      </button>
      <a v-if="joinUrl" class="rb-ob-submit rb-ob-submit-secondary" :href="joinUrl" target="_blank" rel="noopener noreferrer">{{ lang === 'fa' ? 'باز کردن لینک آمادهٔ آلوکم' : 'Open the prepared Alocam link' }}</a>
      <button class="rb-ob-submit" type="button" @click="endSession">
        {{ $t('intake.onlineSession.roomLeave') }}
      </button>
      <button class="rb-ob-submit rb-ob-submit-secondary" type="button" @click="goReconnect">
        {{ $t('intake.onlineSession.roomReconnectHint') }}
      </button>
      <button class="rb-ob-submit rb-ob-submit-secondary" type="button" @click="help">
        {{ $t('intake.onlineSession.roomHelp') }}
      </button>
      <p class="rb-ob-status" role="status" aria-live="polite">{{ formStatus }}</p>
      <div class="rb-ob-trust">
        <p>{{ $t('intake.trustPath') }}</p>
        <p>{{ $t('authUi.familyNote') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import {clientError} from '@/intake/clientMessages'
import {legacySessionSupportRoute} from '@/intake/sessionPreparation'
import {
  doctorDisplayName,
  formatSessionListTitle,
  hasAuthToken,
  resolveJoinableSession,
  requestSessionAdmission,
  admissionJoinUrl,
  createActiveCareSupportCase,
} from '@/intake/portalApi'

export default {
  name: 'SessionRoomStep',
  data() {
    return {
      loading: true,
      needsLogin: false,
      session: null,
      formStatus: '',
      admittedUrl: '',
      joining: false,
      generation: 0,
    }
  },
  computed: {
    lang() {
      return this.$route.params.lang || 'fa'
    },
    therapistName() {
      return doctorDisplayName(this.session && this.session.doctor, this.lang)
          || this.$t('intake.onlineSession.roomTherapistBadge')
    },
    sessionTime() {
      return formatSessionListTitle(this.session, this.lang) || '—'
    },
    canEnter() {
      return !!(this.session && this.session.can_enter)
    },
    joinUrl() {
      return this.admittedUrl
    },
    connLabel() {
      if (this.joinUrl) return this.$t('intake.onlineSession.roomConnLinked')
      if (this.canEnter) return this.lang === 'fa' ? 'اجازهٔ ورود آماده است؛ اتصال در آلوکم بررسی می‌شود.' : 'Entry is available; connection is checked in Alocam.'
      return this.$t('intake.onlineSession.joinPending')
    },
  },
  mounted() {
    this.bootstrap()
  },
  watch: {'$route.query.id'() { return this.bootstrap() }},
  beforeDestroy() { this.generation += 1 },
  methods: {
    sessionQuery() {
      return this.session && this.session.id ? {id: String(this.session.id)} : {}
    },
    bootstrap() {
      const generation = ++this.generation
      this.loading = true
      this.session = null
      this.admittedUrl = ''
      this.formStatus = ''
      this.needsLogin = !hasAuthToken()
      if (this.needsLogin) { this.loading = false; return }
      const preferred = this.$route.query.id || this.$route.params.sessionId
      return resolveJoinableSession(preferred)
          .then((session) => {
            if (generation === this.generation) this.session = session
          })
          .catch((e) => { if (generation === this.generation) this.formStatus = clientError(e, this.lang) })
          .finally(() => {
            if (generation === this.generation) this.loading = false
          })
    },
    openJoin() {
      if (!this.session || !this.session.id || this.joining || !this.canEnter) return
      const generation = this.generation
      this.joining = true
      this.formStatus = ''
      this.admittedUrl = ''
      return requestSessionAdmission(this.session.id).then((admission) => {
        if (generation !== this.generation) return
        const url = admissionJoinUrl(admission)
        if (!url) throw new Error('SESSION_JOIN_CREDENTIAL_UNAVAILABLE')
        this.admittedUrl = url
        window.open(url, '_blank', 'noopener')
        this.formStatus = this.lang === 'fa' ? 'لینک ورود آماده است. اگر برگهٔ آلوکم باز نشد، از لینک زیر استفاده کن.' : 'Your entry link is ready. If the Alocam tab did not open, use the prepared link.'
      }).catch((e) => { if (generation === this.generation) this.formStatus = clientError(e, this.lang) })
        .finally(() => { if (generation === this.generation) this.joining = false })
    },
    endSession() {
      const q = this.sessionQuery()
      this.$router.push({
        name: 'intakeSessionEnd',
        params: {lang: this.lang},
        query: q,
      })
    },
    goReconnect() {
      this.$router.push({
        name: 'intakeSessionReconnect',
        params: {lang: this.lang},
        query: this.sessionQuery(),
      })
    },
    help() {
      const route = legacySessionSupportRoute(this.session, this.lang, 'room')
      if (route) {
        this.formStatus = ''
        return this.$router.push(route)
      }
      return createActiveCareSupportCase('ACCESS', 'Client requested help from the session room.').then(() => {
        this.formStatus = this.$t('intake.onlineSession.supportStatus')
      }).catch((e) => { this.formStatus = clientError(e, this.lang) })
    },
  },
}
</script>
